import { useQuery } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { useState } from 'react';
import { deliveryControls as contract } from '@bb/contracts';
import { Checkbox, Field, Input, RadioGroup, Select } from '@bb/ui';
import { ApiError, type AdminApi, type CampaignSummary } from '../../../api/client.ts';
import { CHANNEL_LABELS } from '../labels.ts';
import { NumberInput } from '../trigger/NumberInput.tsx';
import { toSeconds, UNIT_SECONDS, unitOf, type DelayUnit } from '../trigger/actionBased.ts';
import {
  DEFAULT_QUIET_HOURS,
  deliveryControlsIssues,
  EXEMPTIONS,
  MAX_RATE_PER_MINUTE,
  RATE_DELAY_MAX_HOURS,
  REELIGIBILITY_MAX_DAYS,
  reeligibilityMode,
  spansMidnight,
  switchReeligibility,
  withControl,
  type DeliveryControls,
  type DeliveryControlsValue,
  type QuietHours,
  type ReeligibilityMode,
} from './deliveryControls.ts';

export { deliveryControlsIssues } from './deliveryControls.ts';
export type { DeliveryControlsValue } from './deliveryControls.ts';

export interface DeliveryControlsFormProps {
  environmentId: string;
  /** A transactional campaign skips every control but re-eligibility and never counts towards caps, whatever is stored. */
  category: CampaignSummary['category'];
  /** `content.deliveryControls` and `content.exemptions` of the draft; nothing stored reads as no controls and no exemptions. */
  value: Partial<DeliveryControlsValue> | undefined;
  /** Every edit, valid or not, with both parts: the form shows what the contract rejects (see `deliveryControlsIssues`). */
  onChange: (next: DeliveryControlsValue) => void;
  /** The employee holds `messages.exemptions.manage`; without it the exemptions are shown but cannot change. */
  canManageExemptions: boolean;
  /** A launched revision: nothing can change. */
  readOnly?: boolean;
  /** The last failed draft save; `exemption_not_allowed` (403) is explained at the exemptions. */
  saveError?: unknown;
}

const REELIGIBILITY: Array<{ value: ReeligibilityMode; label: string }> = [
  { value: 'once', label: 'No, only once' },
  { value: 'always', label: 'Yes, every time they qualify' },
  { value: 'after', label: 'Yes, after a wait' },
];
const UNITS: Array<{ value: DelayUnit; label: string }> = [
  { value: 'minutes', label: 'minutes' },
  { value: 'hours', label: 'hours' },
  { value: 'days', label: 'days' },
];
const FALLBACKS: Array<{ value: QuietHours['fallback']; label: string }> = [
  { value: 'next_available', label: 'Send it when quiet hours end' },
  { value: 'abort', label: "Don't send it" },
];

function Errors({ messages }: { messages: Array<string | undefined> }) {
  return (
    <>
      {messages.filter(Boolean).map((message) => (
        <p key={message} className="bb-field__error" role="alert">
          {message}
        </p>
      ))}
    </>
  );
}

/** «After N minutes / hours / days»: the unit stays as chosen, the window is stored in seconds; empty or zero is no window yet (`NaN`). */
function ReeligibilityWindow({ seconds, error, readOnly, onChange }: { seconds: number; error: string | undefined; readOnly: boolean; onChange: (seconds: number) => void }) {
  const [unit, setUnit] = useState<DelayUnit>(() => unitOf(seconds));
  const amount = Number.isFinite(seconds) && seconds > 0 ? seconds / UNIT_SECONDS[unit] : undefined;
  const emit = (n: number | undefined, u: DelayUnit) => onChange(n === undefined || n <= 0 ? Number.NaN : toSeconds(n, u) || Number.NaN);
  return (
    <div className="audience-filter">
      <Field label="Wait" error={error}>
        <NumberInput min={1} step={1} value={amount} disabled={readOnly} onValueChange={(n) => emit(n, unit)} />
      </Field>
      <Field label="Unit">
        <Select
          options={UNITS}
          value={unit}
          disabled={readOnly}
          onChange={(e) => {
            const next = e.target.value as DelayUnit;
            setUnit(next);
            if (amount !== undefined) emit(amount, next);
          }}
        />
      </Field>
    </div>
  );
}

/** Quiet hours of a campaign, or of a Canvas Message step (BB-115 override). */
export function QuietHoursFields({ quiet, issues, readOnly, onChange }: { quiet: QuietHours | undefined; issues: Record<string, string>; readOnly: boolean; onChange: (next: QuietHours) => void }) {
  const on = quiet?.enabled === true;
  return (
    <fieldset className="audience-group">
      <legend>Quiet hours</legend>
      <Checkbox label="Don't send during quiet hours" checked={on} disabled={readOnly} onCheckedChange={(enabled) => onChange(quiet ? { ...quiet, enabled } : { ...DEFAULT_QUIET_HOURS, enabled })} />
      {quiet && on ? (
        <>
          <div className="audience-filter">
            <Field label="From" error={issues['quietHours.from']}>
              <Input type="time" value={quiet.from} disabled={readOnly} onChange={(e) => onChange({ ...quiet, from: e.target.value })} />
            </Field>
            <Field label="To" error={issues['quietHours.to']}>
              <Input type="time" value={quiet.to} disabled={readOnly} onChange={(e) => onChange({ ...quiet, to: e.target.value })} />
            </Field>
          </div>
          {spansMidnight(quiet) && (
            <p className="hint">
              Spans midnight: nothing is sent from {quiet.from} until {quiet.to} the next day.
            </p>
          )}
          <p className="hint">In each user&apos;s time zone; users without one follow the environment time zone.</p>
          <RadioGroup
            label="A send that falls in quiet hours"
            options={FALLBACKS.map((f) => ({ ...f, disabled: readOnly }))}
            value={quiet.fallback}
            onValueChange={(fallback) => onChange({ ...quiet, fallback: fallback as QuietHours['fallback'] })}
          />
          <p className="hint">A send held back until quiet hours end is checked against frequency caps again when it goes out.</p>
        </>
      ) : (
        // Stored but switched off: what would block a save is still shown.
        <Errors messages={[issues['quietHours.from'], issues['quietHours.to']]} />
      )}
    </fieldset>
  );
}

/** The environment's channel limits, so the campaign rate can be set against them; nothing is shown if they cannot be read. */
function EnvironmentLimits({ environmentId }: { environmentId: string }) {
  const { api } = useRouter().options.context as { api: AdminApi };
  const limits = useQuery({ queryKey: ['rate-limits', environmentId], queryFn: () => api.rateLimits(environmentId), retry: false });
  if (!limits.data) return null;
  const set = limits.data.limits;
  return (
    <p className="hint" aria-label="Environment rate limits">
      {set.length === 0
        ? 'The environment sets no channel limits.'
        : `Environment limits: ${set.map((l) => `${CHANNEL_LABELS[l.channel as keyof typeof CHANNEL_LABELS] ?? l.channel} ${l.perMinute.toLocaleString('en-US')} per minute`).join(', ')}.`}
    </p>
  );
}

export interface SendingRateFieldsProps {
  environmentId: string;
  ratePerMinute: number | undefined;
  rateMaxDelayHours: number | undefined;
  /** Issues keyed by field (`ratePerMinute`, `rateMaxDelayHours`), as `deliveryControlsIssues` gives them. */
  issues: Record<string, string>;
  readOnly: boolean;
  /** Label of the per-minute field: «Campaign sends per minute», or the Canvas one (BB-113). */
  rateLabel?: string;
  ratePlaceholder?: string;
  onChange: (patch: { ratePerMinute?: number | undefined; rateMaxDelayHours?: number | undefined }) => void;
}

/** Sends per minute of one campaign or Canvas and how long a held-back send may wait; shown against the environment limits. */
export function SendingRateFields({ environmentId, ratePerMinute, rateMaxDelayHours, issues, readOnly, rateLabel = 'Campaign sends per minute', ratePlaceholder = 'No campaign limit', onChange }: SendingRateFieldsProps) {
  return (
    <fieldset className="audience-group">
      <legend>Sending rate</legend>
      <Field label={rateLabel} hint="Empty: only the environment limits apply. With both, the lower one applies." error={issues.ratePerMinute}>
        <NumberInput min={1} max={MAX_RATE_PER_MINUTE} step={1} placeholder={ratePlaceholder} value={ratePerMinute} disabled={readOnly} onValueChange={(n) => onChange({ ratePerMinute: n })} />
      </Field>
      <EnvironmentLimits environmentId={environmentId} />
      <Field label="Maximum delay (hours)" hint={`A send held back by rate limits for longer is dropped. Empty: ${RATE_DELAY_MAX_HOURS} hours, the most.`} error={issues.rateMaxDelayHours}>
        <NumberInput min={1} max={RATE_DELAY_MAX_HOURS} step={1} placeholder={String(RATE_DELAY_MAX_HOURS)} value={rateMaxDelayHours} disabled={readOnly} onValueChange={(n) => onChange({ rateMaxDelayHours: n })} />
      </Field>
    </fieldset>
  );
}

/**
 * Delivery controls of a campaign (BB-102, CMP-005/007/008): whether a user can get it again, the hours it does not
 * send, its sending rate, and what it may skip (exemptions). Controlled: every edit is emitted with both parts, and
 * what the contract rejects is shown inline. Exemptions need `messages.exemptions.manage`; frequency caps and
 * environment rate limits are settings of the environment, not of the campaign.
 */
export function DeliveryControlsForm({ environmentId, category, value, onChange, canManageExemptions, readOnly = false, saveError }: DeliveryControlsFormProps) {
  const controls: DeliveryControls = value?.deliveryControls ?? {};
  const exemptions = value?.exemptions ?? {};
  const issues = deliveryControlsIssues(controls);
  const transactional = category === 'transactional';
  const effective = contract.effectiveExemptions(category, exemptions);
  const exemptionsLocked = readOnly || !canManageExemptions || transactional;
  const setControl = <K extends keyof DeliveryControls>(key: K, next: DeliveryControls[K] | undefined) => onChange({ deliveryControls: withControl(controls, key, next), exemptions });
  const reeligibility = controls.reeligibility;
  const mode = reeligibilityMode(reeligibility);
  const exemptionDenied = saveError instanceof ApiError && saveError.code === 'exemption_not_allowed';

  return (
    <section className="audience-builder" aria-label="Delivery controls">
      {transactional && (
        <p className="notice notice--neutral">
          Transactional campaign: it skips quiet hours, rate limits, frequency caps, suppression lists and the global control group, and does not count towards frequency caps, whatever is set below. Re-eligibility still
          applies.
        </p>
      )}

      <fieldset className="audience-group">
        <legend>Re-eligibility</legend>
        <RadioGroup
          label="Can a user get this campaign again?"
          options={REELIGIBILITY.map((r) => ({ ...r, disabled: readOnly }))}
          value={mode}
          onValueChange={(next) => setControl('reeligibility', switchReeligibility(reeligibility, next as ReeligibilityMode))}
        />
        {mode === 'after' && reeligibility && (
          <>
            <ReeligibilityWindow seconds={reeligibility.windowSeconds} error={issues['reeligibility.windowSeconds']} readOnly={readOnly} onChange={(windowSeconds) => setControl('reeligibility', { enabled: true, windowSeconds })} />
            <p className="hint">Counted from when the user got it; for a content card, from its first impression. Up to {REELIGIBILITY_MAX_DAYS} days.</p>
          </>
        )}
      </fieldset>

      <QuietHoursFields quiet={controls.quietHours} issues={issues} readOnly={readOnly} onChange={(quietHours) => setControl('quietHours', quietHours)} />

      <SendingRateFields
        environmentId={environmentId}
        ratePerMinute={controls.ratePerMinute}
        rateMaxDelayHours={controls.rateMaxDelayHours}
        issues={issues}
        readOnly={readOnly}
        onChange={(patch) => {
          const [key] = Object.keys(patch) as Array<'ratePerMinute' | 'rateMaxDelayHours'>;
          setControl(key!, patch[key!]);
        }}
      />

      <fieldset className="audience-group">
        <legend>Exemptions</legend>
        <p className="hint">
          {transactional ? 'Shown as they apply to a transactional campaign.' : 'What this campaign may skip. Transactional campaigns, such as trading alerts, skip all of these without exemptions.'}
        </p>
        {!canManageExemptions && !readOnly && !transactional && <p className="hint">Changing exemptions needs the messages.exemptions.manage permission.</p>}
        {EXEMPTIONS.map(({ flag, label, hint }) => (
          <div key={flag}>
            <Checkbox label={label} checked={effective[flag]} disabled={exemptionsLocked} onCheckedChange={(on) => onChange({ deliveryControls: controls, exemptions: { ...exemptions, [flag]: on } })} />
            <p className="hint">{hint}</p>
          </div>
        ))}
        {exemptionDenied && (
          <p className="bb-field__error" role="alert">
            The draft was not saved: changing exemptions needs the messages.exemptions.manage permission. Put them back as they were to save the other changes.
          </p>
        )}
      </fieldset>

      <Errors messages={[issues.deliveryControls]} />
      {readOnly && <p className="hint">This revision is launched: its delivery controls cannot change.</p>}
    </section>
  );
}
